import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { PageHeader, LoadingSpinner } from '../components/UIComponents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { prospectsAPI, rfqAPI } from '../utils/api';
import {
  Plus, Search, Building2, Mail, Phone, MapPin, ChevronRight,
  Users, UserPlus, Inbox, Clock, AlertTriangle, CheckCircle2,
  XCircle, ArrowRightLeft, Upload
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

// ==================== PROSPECT STATUS CONFIG ====================

const PROSPECT_STATUS_CONFIG = {
  NEW: { label: 'New', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  CONTACTED: { label: 'Contacted', bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200' },
  DATA_PENDING: { label: 'Data Pending', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  DATA_RECEIVED: { label: 'Data Received', bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
  CONFIGURED: { label: 'Configured', bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  PROPOSAL_SENT: { label: 'Proposal Sent', bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' },
  WON: { label: 'Won', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  LOST: { label: 'Lost', bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' },
};

const ProspectStatusBadge = ({ status }) => {
  const cfg = PROSPECT_STATUS_CONFIG[status] || PROSPECT_STATUS_CONFIG.NEW;
  return (
    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
      {cfg.label}
    </span>
  );
};

// ==================== RFQ STATUS CONFIG ====================

const RFQ_STATUS_CONFIG = {
  NEW: { label: 'New', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  UNDER_REVIEW: { label: 'Under Review', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  NEEDS_MORE_INFO: { label: 'Needs Info', bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  APPROVED: { label: 'Approved', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  REJECTED: { label: 'Rejected', bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' },
  CONVERTED: { label: 'Converted', bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200' },
};

const RFQStatusBadge = ({ status }) => {
  const cfg = RFQ_STATUS_CONFIG[status] || RFQ_STATUS_CONFIG.NEW;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
      {cfg.label}
    </span>
  );
};

const RFQ_FILTER_TABS = ['ALL', 'NEW', 'UNDER_REVIEW', 'NEEDS_MORE_INFO', 'APPROVED', 'REJECTED', 'CONVERTED'];

// ==================== PROSPECTS TAB CONTENT ====================

const ProspectsTab = ({ navigate }) => {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ company_name: '', contact_name: '', email: '', phone: '', industry: '', city: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchProspects(); }, []);

  const fetchProspects = async () => {
    try {
      const res = await prospectsAPI.getAll();
      setProspects(res.data);
    } catch (e) { toast.error(e.userMessage || 'Failed to load prospects'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await prospectsAPI.create(form);
      toast.success('Prospect created');
      setShowAdd(false);
      setForm({ company_name: '', contact_name: '', email: '', phone: '', industry: '', city: '', notes: '' });
      fetchProspects();
    } catch (e) { toast.error(e.userMessage || 'Failed to create prospect'); }
    finally { setSubmitting(false); }
  };

  const filtered = prospects.filter(p => {
    const matchSearch = !search || p.company_name.toLowerCase().includes(search.toLowerCase()) ||
      p.contact_name.toLowerCase().includes(search.toLowerCase()) || p.city?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'ALL' || p.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const statusCounts = prospects.reduce((acc, p) => { acc[p.status] = (acc[p.status] || 0) + 1; return acc; }, {});

  if (loading) return <LoadingSpinner />;

  return (
    <>
      {/* Action bar */}
      <div className="flex items-center justify-between mb-5">
        <div className="relative max-w-md flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search prospects..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
            data-testid="search-prospects"
          />
        </div>
        <Button onClick={() => setShowAdd(true)} data-testid="add-prospect-btn" className="bg-slate-900 hover:bg-slate-800 ml-3">
          <Plus className="w-4 h-4 mr-2" /> New Prospect
        </Button>
      </div>

      {/* Status pipeline */}
      <div className="flex flex-wrap gap-1.5 mb-5" data-testid="status-pipeline">
        <button
          onClick={() => setFilterStatus('ALL')}
          className={`text-xs font-medium px-3 py-1.5 rounded-sm border transition-colors ${
            filterStatus === 'ALL' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
          }`}
        >
          All ({prospects.length})
        </button>
        {Object.entries(PROSPECT_STATUS_CONFIG).map(([key, cfg]) => (
          <button
            key={key}
            onClick={() => setFilterStatus(key)}
            className={`text-xs font-medium px-3 py-1.5 rounded-sm border transition-colors ${
              filterStatus === key ? `${cfg.bg} ${cfg.text} ${cfg.border}` : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
            }`}
          >
            {cfg.label} ({statusCounts[key] || 0})
          </button>
        ))}
      </div>

      {/* Prospects grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <UserPlus className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500">No prospects found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(p => (
            <div
              key={p.prospect_id}
              onClick={() => navigate(`/prospects/${p.prospect_id}`)}
              data-testid={`prospect-card-${p.prospect_id}`}
              className="bg-white border border-slate-200 rounded-sm p-5 hover:border-slate-300 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 cursor-pointer group"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3 min-w-0">
                  <div className="w-10 h-10 bg-indigo-50 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-heading font-semibold text-slate-900 text-sm truncate">{p.company_name}</h3>
                    <p className="text-xs text-slate-500 font-mono">{p.site_count || 0} site{(p.site_count || 0) !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 flex-shrink-0">
                  <ProspectStatusBadge status={p.status} />
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors" />
                </div>
              </div>
              <div className="space-y-1.5 text-sm text-slate-600">
                <div className="flex items-center"><Users className="w-3.5 h-3.5 mr-2 text-slate-400" />{p.contact_name}</div>
                {p.email && <div className="flex items-center"><Mail className="w-3.5 h-3.5 mr-2 text-slate-400" /><span className="truncate">{p.email}</span></div>}
                {p.city && <div className="flex items-center"><MapPin className="w-3.5 h-3.5 mr-2 text-slate-400" />{p.city}{p.industry ? ` · ${p.industry}` : ''}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New Prospect</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div>
              <Label>Company Name *</Label>
              <Input value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} required className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Contact Name *</Label><Input value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} required className="mt-1" /></div>
              <div><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="mt-1" /></div>
              <div><Label>Industry</Label><Input value={form.industry} onChange={e => setForm({...form, industry: e.target.value})} placeholder="e.g., Manufacturing" className="mt-1" /></div>
            </div>
            <div><Label>City</Label><Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="mt-1" /></div>
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-slate-900">{submitting ? 'Creating...' : 'Create Prospect'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ==================== INBOUND RFQs TAB CONTENT ====================

const InboundRFQsTab = ({ navigate }) => {
  const [rfqs, setRfqs] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [rfqRes, countsRes] = await Promise.all([rfqAPI.list(), rfqAPI.getCounts()]);
      setRfqs(rfqRes.data);
      setCounts(countsRes.data);
    } catch (e) {
      toast.error(e.userMessage || 'Failed to load RFQs');
    } finally {
      setLoading(false);
    }
  };

  const filtered = rfqs.filter(r => {
    const matchSearch = !search ||
      r.company_name?.toLowerCase().includes(search.toLowerCase()) ||
      r.contact_name?.toLowerCase().includes(search.toLowerCase()) ||
      r.contact_email?.toLowerCase().includes(search.toLowerCase()) ||
      r.rfq_id?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'ALL' || r.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    const hrs = Math.floor(diff / 3600000);
    if (hrs < 1) return 'Just now';
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  if (loading) return <LoadingSpinner />;

  return (
    <>
      {/* Status filter tabs */}
      <div className="flex flex-wrap gap-1.5 mb-5" data-testid="rfq-status-filters">
        {RFQ_FILTER_TABS.map(tab => {
          const cfg = RFQ_STATUS_CONFIG[tab];
          const count = counts[tab] || 0;
          const isActive = filterStatus === tab;
          return (
            <button
              key={tab}
              data-testid={`rfq-filter-${tab.toLowerCase()}`}
              onClick={() => setFilterStatus(tab)}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-all border ${
                isActive
                  ? 'bg-slate-800 text-white border-slate-800'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
              }`}
            >
              {tab === 'ALL' ? 'All' : (cfg?.label || tab)}
              <span className={`ml-1.5 ${isActive ? 'text-slate-300' : 'text-slate-400'}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="mb-5 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search company, contact, email, or RFQ ID..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9 h-9 text-sm"
          data-testid="rfq-search-input"
        />
      </div>

      {/* RFQ Table */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border border-slate-200">
          <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">No RFQs found</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full text-sm" data-testid="rfq-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 font-medium text-slate-500 text-xs uppercase tracking-wider">RFQ ID</th>
                <th className="text-left px-4 py-3 font-medium text-slate-500 text-xs uppercase tracking-wider">Company</th>
                <th className="text-left px-4 py-3 font-medium text-slate-500 text-xs uppercase tracking-wider">Contact</th>
                <th className="text-left px-4 py-3 font-medium text-slate-500 text-xs uppercase tracking-wider hidden md:table-cell">Created</th>
                <th className="text-center px-4 py-3 font-medium text-slate-500 text-xs uppercase tracking-wider hidden lg:table-cell">Sites</th>
                <th className="text-center px-4 py-3 font-medium text-slate-500 text-xs uppercase tracking-wider hidden lg:table-cell">Uploads</th>
                <th className="text-left px-4 py-3 font-medium text-slate-500 text-xs uppercase tracking-wider">Status</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(rfq => (
                <tr
                  key={rfq.rfq_id}
                  data-testid={`rfq-row-${rfq.rfq_id}`}
                  className="border-b border-slate-100 hover:bg-slate-50/70 cursor-pointer transition-colors"
                  onClick={() => navigate(`/rfq/${rfq.rfq_id}`)}
                >
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs text-slate-500">#{rfq.rfq_id.substring(0, 8)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center shrink-0">
                        <Building2 className="w-4 h-4 text-slate-400" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800 text-sm leading-tight">{rfq.company_name}</p>
                        {rfq.city && (
                          <p className="text-[11px] text-slate-400 flex items-center gap-0.5 mt-0.5">
                            <MapPin className="w-3 h-3" />{rfq.city}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-slate-700 text-sm">{rfq.contact_name}</p>
                    <p className="text-[11px] text-slate-400 flex items-center gap-0.5 mt-0.5">
                      <Mail className="w-3 h-3" />{rfq.contact_email}
                    </p>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <p className="text-xs text-slate-500">{formatDate(rfq.created_at)}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{timeAgo(rfq.created_at)}</p>
                  </td>
                  <td className="px-4 py-3 text-center hidden lg:table-cell">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-slate-100 text-xs font-medium text-slate-600">
                      {rfq.site_count}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center hidden lg:table-cell">
                    {rfq.has_uploads ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                        <Upload className="w-3 h-3" />{rfq.upload_count}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <RFQStatusBadge status={rfq.status} />
                  </td>
                  <td className="px-2 py-3">
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

// ==================== MAIN PAGE ====================

export default function ProspectsListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const activeTab = searchParams.get('tab') || 'prospects';

  // Engineering users can only see the Prospects tab
  const canSeeRFQ = ['super_admin', 'admin', 'management', 'sales'].includes(user?.role);

  const setTab = (tab) => {
    setSearchParams(tab === 'prospects' ? {} : { tab });
  };

  return (
    <div className="flex min-h-screen bg-slate-50/50" data-testid="prospects-page">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-6 md:p-8 max-w-[1920px] mx-auto space-y-5">
          <PageHeader
            title="Prospects"
            description="Pre-sales pipeline — manage leads, site assessments, and inbound RFQs"
          />

          {/* Tab switcher */}
          {canSeeRFQ && (
            <div className="flex border-b border-slate-200" data-testid="prospects-tabs">
              <button
                onClick={() => setTab('prospects')}
                data-testid="tab-prospects"
                className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                  activeTab === 'prospects'
                    ? 'border-slate-800 text-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <UserPlus className="w-4 h-4 inline mr-2 -mt-0.5" />
                Prospects
              </button>
              <button
                onClick={() => setTab('rfq')}
                data-testid="tab-inbound-rfqs"
                className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                  activeTab === 'rfq'
                    ? 'border-slate-800 text-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <Inbox className="w-4 h-4 inline mr-2 -mt-0.5" />
                Inbound RFQs
              </button>
            </div>
          )}

          {/* Tab content */}
          {activeTab === 'prospects' && <ProspectsTab navigate={navigate} />}
          {activeTab === 'rfq' && canSeeRFQ && <InboundRFQsTab navigate={navigate} />}
        </div>
      </div>
    </div>
  );
}
